'use client'

import { useState, useCallback } from 'react'
import { cn } from '@/lib/utils'

// ============================================================================
// Constants
// ============================================================================

const AVATAR_COLORS = [
  '#6252fd', // brand purple
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
] as const

const SIZE_CONFIG = {
  /** Inline-chip size — fits inside a text-flow pill (client reference chips). */
  xxs: { container: 'size-4', img: 12, text: 'text-[7px]' },
  xs: { container: 'size-7', img: 18, text: 'text-[10px]' },
  sm: { container: 'size-8', img: 18, text: 'text-xs' },
  md: { container: 'size-9', img: 22, text: 'text-sm' },
  lg: { container: 'size-10', img: 24, text: 'text-sm' },
  xl: { container: 'size-14', img: 32, text: 'text-base' },
} as const

// ============================================================================
// Utils (exported for reuse where only the URL/domain is needed)
// ============================================================================

export function getDomainFromEmail(email: string): string | null {
  const match = email.match(/@([^@]+)$/)
  return match ? match[1] : null
}

export function getDomainFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`)
    return parsed.hostname
  } catch {
    return null
  }
}

export function getClientDomain(
  website: string | null | undefined,
  email: string | null | undefined,
): string | null {
  return (website ? getDomainFromUrl(website) : null) ?? (email ? getDomainFromEmail(email) : null)
}

export function getFaviconUrl(
  website: string | null | undefined,
  email: string | null | undefined,
  size: number = 64,
): string | null {
  const domain = getClientDomain(website, email)
  if (!domain) return null
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=${size}`
}

function getColorFromName(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

/**
 * Which favicon URLs have already been proved to have no icon behind them.
 *
 * Google's s2 endpoint answers for an unknown domain with a 16px default globe
 * rather than a 404, so the only way to learn that a client has no favicon is
 * to fetch it and look at what came back. Without a memory, every mount of
 * every row repeated that: entering Claims fetched 18 favicons that were
 * already known to be missing, then flipped 18 discs into place across seven
 * commits and three later layout frames.
 *
 * The globe is the only thing written here. A failed request says something
 * about the network, not about the domain, and caching that would keep a real
 * favicon hidden until reload.
 *
 * Module level, so the lifetime is the tab. That is the right lifetime here: a
 * client that adds a website gets its favicon on the next reload, and nothing
 * needs invalidating in between.
 */
const MISSING_FAVICONS = new Set<string>()

// ============================================================================
// Component
// ============================================================================

interface ClientFaviconProps {
  /** Company name — used for initials fallback and deterministic color */
  name: string
  /** Company website URL */
  website?: string | null
  /** Contact email — domain is extracted as fallback for favicon */
  email?: string | null
  /** Predefined size preset */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** Container shape */
  shape?: 'rounded' | 'circle'
  /** Additional container class names */
  className?: string
  /**
   * Overrides the deterministic colour of the initials disc shown when there is
   * no favicon. Pass one when the surface already gives the client a colour, so
   * the disc and the surface do not argue. Ignored once a favicon loads.
   */
  fallbackColor?: string | null
  /**
   * Container element. Use 'span' when the favicon renders inside phrasing
   * content (e.g. inline chips in markdown <p> elements) — a <div> there is
   * invalid HTML and breaks hydration.
   */
  as?: 'div' | 'span'
}

export function ClientFavicon({
  name,
  website,
  email,
  size = 'md',
  shape = 'rounded',
  className,
  fallbackColor,
  as: Container = 'div',
}: ClientFaviconProps) {
  const faviconUrl = getFaviconUrl(website, email, 64)
  // A URL already known to have nothing behind it draws its disc on the first
  // commit, with no <img>, no request and no state update.
  const [imgError, setImgError] = useState(() =>
    faviconUrl !== null && MISSING_FAVICONS.has(faviconUrl),
  )

  const config = SIZE_CONFIG[size]
  const color = fallbackColor ?? getColorFromName(name)
  const initials = getInitials(name)
  const showFavicon = faviconUrl != null && !imgError

  // A client that gains or changes a website while the row is mounted asks about
  // the new URL rather than keeping the disc it settled on. Adjusted during
  // render, which is React's own answer for state derived from a prop: an
  // effect would paint the stale disc first and then correct it.
  const [lastUrl, setLastUrl] = useState(faviconUrl)
  if (lastUrl !== faviconUrl) {
    setLastUrl(faviconUrl)
    setImgError(faviconUrl !== null && MISSING_FAVICONS.has(faviconUrl))
  }

  // An error is not an answer about the domain: it fires on a network blip, a
  // captive portal, a rate limit or a content blocker on google.com/s2. It
  // hides the broken image on this instance and nothing more, so the next mount
  // tries again.
  const handleError = useCallback(() => setImgError(true), [])

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget
      // Google returns a tiny 16x16 default globe for unknown domains, which IS
      // an answer: this domain has no icon, and it will have none on the next
      // mount either. That is the only verdict worth remembering.
      if (img.naturalWidth > 16) return
      if (faviconUrl !== null) MISSING_FAVICONS.add(faviconUrl)
      setImgError(true)
    },
    [faviconUrl],
  )

  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg'

  if (showFavicon) {
    return (
      <Container
        className={cn(
          'inline-flex shrink-0 items-center justify-center overflow-hidden bg-muted',
          shapeClass,
          config.container,
          className,
        )}
      >
        <img
          src={faviconUrl}
          alt=""
          width={config.img}
          height={config.img}
          className="object-contain"
          onError={handleError}
          onLoad={handleLoad}
        />
      </Container>
    )
  }

  return (
    <Container
      className={cn(
        'inline-flex shrink-0 items-center justify-center font-semibold text-white',
        shapeClass,
        config.container,
        config.text,
        className,
      )}
      style={{ backgroundColor: color }}
      aria-label={name}
    >
      {initials}
    </Container>
  )
}
